import type { MapPoint, MapSupport } from '@re/form-ui';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from './client';

const ORIGIN = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, '') ?? '';
const PUBLIC = `${ORIGIN}/api/v1/public`;

/** «إعدادات الخريطة» of the company (Settings → الخريطة). */
export interface MapSettings {
  workGovernorateIds: number[];
  workAreaIds: number[];
  lockToWorkArea: boolean;
  style: 'satellite' | 'streets';
  colorBy: 'kind' | 'ppm';
  showApprox: boolean;
  rentPeriod: 'year' | 'month';
  sharePrecision: 'exact' | 'approx' | 'hidden';
}
export interface MapView {
  settings: MapSettings;
  /** [west, south, east, north] of the work area (or the company's country) */
  bbox: [number, number, number, number] | null;
  center: MapPoint | null;
  countryIso2: string | null;
}

/** A Google Maps link (short ones too) → its point, read by the server. */
export async function resolveMapLink(url: string): Promise<MapPoint | null> {
  const r = await fetch(`${PUBLIC}/geo/resolve-link`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url }) });
  if (!r.ok) throw new Error(`resolve-link ${r.status}`);
  const p = (await r.json()) as { lat: number | null; lng: number | null };
  return typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;
}

const AMMAN: MapPoint = { lat: 31.9539, lng: 35.9106 };

export function useMapView() {
  return useQuery({ queryKey: ['map-view'], queryFn: () => api<MapView>('/map-view'), staleTime: 5 * 60_000 });
}

/** The map key (from the server) + the link reader + where the company works, for the forms and the map page. */
export function useMapSupport(): MapSupport & { view: MapView | undefined } {
  const cfg = useQuery({
    queryKey: ['map-config'],
    queryFn: async () => (await (await fetch(`${PUBLIC}/map-config`)).json()) as { mapboxToken: string | null },
    staleTime: Infinity,
  });
  const view = useMapView();
  return useMemo(
    () => ({
      token: cfg.data?.mapboxToken ?? null,
      resolveLink: resolveMapLink,
      defaultCenter: view.data?.center ?? AMMAN,
      bbox: view.data?.bbox ?? null,
      view: view.data,
    }),
    [cfg.data, view.data],
  );
}
